import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { DealApplicationRequirement } from 'app/services/deals/deal-application-requirement.model';
import { DealDocumentsService } from 'app/services/deals/deal-documents.service';
import { catchError, map, take } from 'rxjs/operators';
import { AddDocumentCategory, AttachDealDocument, ClearDealDocumentRequirements, DetachDealDocument, GetDealDocumentRequirements, UploadDealDocument, SetDocumentRequirementsMet } from './deal-document.actions';
import { DealDocumentUtility } from './deal-document-utility';
import _ from 'lodash';
import { DocumentsService } from 'app/services/documents/documents.service';
import { throwError, of } from 'rxjs';
import { Alert } from 'app/models/dto/alert.model';
import { CreateNewDealAlert } from '../deal-alerts/deal-alerts.actions';
import { Injectable } from '@angular/core';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';

export class DealDocumentStateModel {
  uploading: boolean;
  loading: boolean;
  dealDocumentRequirements: {
    dealId: number;
    documentRequirements: {
      contractRequirements: DealApplicationRequirement[];
      offerRequirements: DealApplicationRequirement[];
      otherDocs: any;
    };
    requirementsMet: boolean;
  }[];
}

@State<DealDocumentStateModel>({
	name: 'documentRequirements',
  defaults: {
    uploading: false,
    loading: false,
    dealDocumentRequirements: []
	}
})

@Injectable()

export class DealDocumentState {

  @Selector()
  static dealDocumentRequirements(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealDocumentRequirements.find(requirements => requirements.dealId === dealId);
      return dealDocumentRequirements ? dealDocumentRequirements.documentRequirements : [];
    };
  }

  @Selector()
  static hasSignedApplication(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealDocumentRequirements.find(requirements => requirements.dealId === dealId);
      const signedApplication = dealDocumentRequirements
        ? dealDocumentRequirements.documentRequirements.offerRequirements.find(req => req.category.value === 'applicationSigned')
        : false;
      return (signedApplication && signedApplication.requirement && signedApplication.requirement.length > 0);
    };
  }

  @Selector()
	static uploading(state: DealDocumentStateModel) {
    return state.uploading;
  }

  @Selector()
  static loading(state: DealDocumentStateModel) {
    return state.loading;
  }

  @Selector()
  static requirementsMet(state: DealDocumentStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealDocumentRequirements.filter(requirements => requirements.dealId === dealId);
      return dealDocumentRequirements.length ? dealDocumentRequirements[0].requirementsMet : false;
    };
  }

  constructor(
    private dealDocumentsService: DealDocumentsService,
    private documentsService: DocumentsService,
    private dealDocumentUtility: DealDocumentUtility,
    private store: Store
  ) {}

  @Action(GetDealDocumentRequirements)
	getDealDocumentRequirements(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { deal, loanProductId }: GetDealDocumentRequirements
  ) {
    const alreadyFetchedDealDocumentRequirements = getState().dealDocumentRequirements
      .findIndex(requirements => requirements.dealId === deal.id) !== -1;

    if (alreadyFetchedDealDocumentRequirements) {
      return;
    }

    patchState({ loading: true });
    return this.dealDocumentsService.getDocumentRequirements(loanProductId).pipe(map(documentRequirements => {

      const dealDocumentTypes = this.dealDocumentUtility.docTypes;
      const groupedDealDocuments = _(deal.many_documents).groupBy('category').value();
      const borrowerStateId = this.store.selectSnapshot( BorrowerValueState.valueSelectorByAlias('borrowerState') );
      const hasDisclosureReqPermission = this.store.selectSnapshot( LenderUserState.hasPermission('loanDiscloserRequirement') );
      const disclosureReqState = ['NY','CA'].includes(borrowerStateId?.value || '');

      // if there are documents attached to the deal, attach them to their respective requirement
      if (deal.many_documents) {
        const extras = [this.dealDocumentUtility.signedApplicationRequirement];
        if(hasDisclosureReqPermission && disclosureReqState) {
          extras.push(this.dealDocumentUtility.loanDisclosureRequirement);
        }
        documentRequirements.unshift(...extras);
        documentRequirements.map(requirement => {
          for (const [key, value] of Object.entries(dealDocumentTypes)) {
            if (value.alias === requirement.alias) {
              requirement.category = value;
            }
          }
          if (!requirement.category) {
            requirement.category = {
              display: requirement.name,
              value: requirement.dbName,
              alias: requirement.alias,
            };
          }
          if (requirement.alias === 'application_signed') {
            requirement.requirement = deal.signed_application
              ? [deal.signed_application]
              : [];
          } else if (requirement.alias === 'loanDisclosure') {
            const disclosureDoc = deal.many_documents.filter( doc => doc.category === 'loanDisclosure')[0];
            requirement.requirement = disclosureDoc
              ? [disclosureDoc]
              : [];
          } else {
            requirement.requirement = groupedDealDocuments[requirement.category.value]
              ? groupedDealDocuments[requirement.category.value]
              : [];
            delete groupedDealDocuments[requirement.category.value];
          }
          return requirement;
        });
      }

      // format additional documents into requirement shape for display, if any
      const otherDealDocs: any[] = [];
      if (Object.keys(groupedDealDocuments).length > 0) {
        for (const [key, value] of Object.entries(groupedDealDocuments)) {
          if (!['applicationSigned', 'loanDisclosure'].includes(key) && dealDocumentTypes[key]) {
            otherDealDocs.push({
              name: dealDocumentTypes[key].display,
              requirement: value,
              category: dealDocumentTypes[key],
              requiredForOffer: false,
              requiredForContract: false
            });
          }
        }
      }

      patchState({
        loading: false,
        dealDocumentRequirements: [
          ...getState().dealDocumentRequirements,
          {
            dealId: deal.id,
            documentRequirements: {
              contractRequirements: documentRequirements.filter(req => !req.requiredForOffer && req.requiredForContract),
              offerRequirements: documentRequirements.filter(req => (
                  !req.requiredForContract && req.requiredForOffer
                  || (req.requiredForContract && req.requiredForOffer)
                  || req.alias === 'application_signed'
                )),
              otherDocs: otherDealDocs
            },
            requirementsMet: false
          }
        ]
      });

      this.store.dispatch(new SetDocumentRequirementsMet(deal.id));
    }));
  }

  @Action(UploadDealDocument)
  uploadDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { file, borrowerId, dealId, category, requirementGroup }: UploadDealDocument
  ) {
    patchState({ uploading: true });
    return this.dealDocumentsService.uploadDealDocument(file, borrowerId, dealId, category).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error uploading the deal document. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      patchState({ uploading: false });
      return of();
    }), map(docUpload => {
      // if uploading a signed application, make additional call to update deal with document id
      if (category === 'applicationSigned') {
        this.dealDocumentsService.attachSignedApplication(dealId, docUpload.id).pipe(take(1)).subscribe();
      }
      const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
      const dealRequirementsIndex = getState().dealDocumentRequirements.findIndex(ddr => ddr.dealId === dealId);
      const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
      dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
        if (req.category.value === category) {
          req.requirement.push(docUpload);
        }
      });
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({
        dealDocumentRequirements: dealRequirementsStateCopy,
        uploading: false
      });
      this.store.dispatch(new SetDocumentRequirementsMet(dealId));
    }));
  }

  @Action(AttachDealDocument)
  attachDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, document, requirementGroup }: AttachDealDocument
  ) {
    patchState({ uploading: true });
    return this.documentsService.update({ id: document.id, dealId, attach: true, category: document.category }).pipe(catchError(err => {
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error attaching this document to the deal. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      patchState({ uploading: false });
      return of();
    }), map(updatedDoc => {
      const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
      const dealRequirementsIndex = getState().dealDocumentRequirements.findIndex(ddr => ddr.dealId === dealId);
      const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
      dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
        if (req.category.value === document.category) {
          req.requirement.push(document);
        }
      });
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({ dealDocumentRequirements: dealRequirementsStateCopy, uploading: false });
      // if attaching a signed application, make additional call to update deal with document id
      if (updatedDoc.category === 'applicationSigned') {
        this.dealDocumentsService.attachSignedApplication(dealId, updatedDoc.id).pipe(take(1)).subscribe();
      }
      this.store.dispatch(new SetDocumentRequirementsMet(dealId));
    }));
  }

  @Action(DetachDealDocument)
  detachDealDocument(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, document, requirementGroup }: DetachDealDocument
  ) {
    const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
    const dealRequirementsIndex = getState().dealDocumentRequirements.findIndex(ddr => ddr.dealId === dealId);
    const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
    dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
      if (req.category.value === document.category) {
        req.requirement = req.requirement.filter(doc => doc.id !== document.id);
      }
    });
    dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
    patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
    return this.documentsService.update({ id: document.id, dealId, detach: true }).pipe(catchError(err => {
      dealRequirementsCopy.documentRequirements[requirementGroup].forEach((req: DealApplicationRequirement) => {
        if (req.category.value === document.category) {
          req.requirement.push(document);
        }
      });
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
      const newAlert: Alert = {
        level: 'error',
        message: 'There was an error detaching this document from the deal. Please try again.',
        dismissDelay: 5000
      };
      this.store.dispatch(new CreateNewDealAlert(dealId, newAlert));
      return of();
    }), map(updatedDoc => {
      // if detaching a signed application, make additional call to update deal
      if (updatedDoc.category === 'applicationSigned') {
        this.dealDocumentsService.detachSignedApplication(dealId).pipe(take(1)).subscribe();
      }
      this.store.dispatch(new SetDocumentRequirementsMet(dealId));
    }));
  }

  @Action(AddDocumentCategory)
  addDocumentCategory(
    { getState, patchState }: StateContext<DealDocumentStateModel>,
    { dealId, documentCategory }: AddDocumentCategory
  ) {
    const dealDocumentTypes = this.dealDocumentUtility.docTypes;
    if (dealDocumentTypes[documentCategory.id]) {
      const newCategory = {
        name: dealDocumentTypes[documentCategory.id].display,
        requirement: [],
        category: dealDocumentTypes[documentCategory.id],
        requiredForOffer: false,
        requiredForContract: false
      };
      const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
      const dealRequirementsIndex = getState().dealDocumentRequirements?.findIndex(ddr => ddr.dealId === dealId);
      const dealRequirementsCopy = { ...getState().dealDocumentRequirements[dealRequirementsIndex] };
      dealRequirementsCopy?.documentRequirements?.otherDocs?.push(newCategory);
      dealRequirementsStateCopy[dealRequirementsIndex] = dealRequirementsCopy;
      patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
    }
  }


  @Action(SetDocumentRequirementsMet)
  setDocumentRequirementsMet({ getState, patchState }: StateContext<DealDocumentStateModel>, { dealId }: SetDocumentRequirementsMet) {
    const dealRequirementsStateCopy = [ ...getState().dealDocumentRequirements ];
    const dealDocumentRequirementsIndex = dealRequirementsStateCopy.findIndex(reqs => reqs.dealId === dealId);
    const dealDocumentRequirements = { ...dealRequirementsStateCopy[dealDocumentRequirementsIndex] };
    const offerRequirements = dealDocumentRequirements?.documentRequirements?.offerRequirements;
    const reqDocsCheck: boolean[] = offerRequirements
      ? offerRequirements.map(req => req.requirement && req.requirement.length && req.requirement.length > 0 ? true : false)
      : [];
    if (reqDocsCheck.includes(false)) {
      dealDocumentRequirements.requirementsMet = false;
    } else {
      dealDocumentRequirements.requirementsMet = true;
    }
    dealRequirementsStateCopy[dealDocumentRequirementsIndex] = dealDocumentRequirements;
    patchState({ dealDocumentRequirements: dealRequirementsStateCopy });
  }

  @Action(ClearDealDocumentRequirements)
  clearDealDocumentRequirements({ patchState }: StateContext<DealDocumentStateModel>, {}: ClearDealDocumentRequirements) {
    patchState({ dealDocumentRequirements: [] });
  }
}
